import { ChevronDownIcon, CustomizeColumnsIcon, MagicIcon } from "@xola/icons";
import { Button, Popover, Switch } from "@xola/ui-kit";
import { Column, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import clsx from "clsx";
import { SecondaryButton } from "common";
import { snakeCase } from "lodash";
import React, { RefObject, useEffect, useState } from "react";
import { Except } from "type-fest";
import { Experience } from "types";
import { useModal } from "../../../../hooks/useVisibility";
import { SearchInput } from "../../../Common/SearchInput";
import { XolaFormik } from "../../../Common/XolaFormik";
import { Loader } from "../../../Loader";
import { useDashboardTableStore } from "../../DashboardTable/useDashboardTableStore";
import {
    getFormUniqFields,
    getPickUpAddOnsUniqFields,
    getPickUpAddOnsUniqFieldsFromMultiple,
    HeaderNames,
    RosterColumnId,
} from "./helpers";
import { useForms } from "@/hooks/resources/useForms";
import { DashboardMode } from "@/types/Dashboard";

const EVENT_DETAILS_DATA = [
    { name: HeaderNames.TIME, colId: RosterColumnId.TIME, isChecked: true },
    { name: HeaderNames.PRODUCT, colId: RosterColumnId.PRODUCT, isChecked: true },
];

const PURCHASE_DETAILS_DATA = [
    { name: HeaderNames.BALANCE_DUE, colId: RosterColumnId.BALANCE_DUE, isChecked: true },
    { name: HeaderNames.GROSS_PURCHASE_AMOUNT, colId: RosterColumnId.GROSS_PURCHASE_AMOUNT, isChecked: false },
    { name: HeaderNames.NOTES, colId: RosterColumnId.NOTES, isChecked: true },
    { name: HeaderNames.PURCHASE_DATE, colId: RosterColumnId.PURCHASE_DATE, isChecked: false },
    { name: HeaderNames.SOURCE, colId: RosterColumnId.SOURCE, isChecked: true },
    { name: HeaderNames.TAGS, colId: RosterColumnId.TAGS, isChecked: true },
];

const RESERVATION_DETAILS_DATA = [
    { name: HeaderNames.ADD_ONS, colId: RosterColumnId.ADDONS, isChecked: true },
    { name: HeaderNames.DEMOGRAPHICS, colId: RosterColumnId.DEMOGRAPHICS, isChecked: true },
    { name: HeaderNames.VOUCHERS, colId: RosterColumnId.VOUCHERS, isChecked: true },
    { name: HeaderNames.GROSS_RESERVATION_AMOUNT, colId: RosterColumnId.GROSS_RESERVATION_AMOUNT, isChecked: false },
    { name: HeaderNames.RESERVATION_CODE, colId: RosterColumnId.RESERVATION_CODE, isChecked: false },
];

const GUEST_DATA = [
    { name: HeaderNames.CHECK_IN, colId: RosterColumnId.CHECK_IN, isChecked: true },
    { name: HeaderNames.EMAIL, colId: RosterColumnId.EMAIL, isChecked: true },
    { name: HeaderNames.GUEST, colId: RosterColumnId.GUEST, isChecked: true },
    { name: HeaderNames.PHONE, colId: RosterColumnId.PHONE, isChecked: true },
    { name: HeaderNames.WAIVERS, colId: RosterColumnId.WAIVERS, isChecked: true },
];

interface CustomizeColumnsSectionProps {
    experience: Experience | Experience[];
    context?: DashboardMode;
    gridRef?: RefObject<AgGridReact>;
    onClose: () => void;
}

interface InitialValues {
    eventDetails: { [key: string]: boolean };
    purchaseDetails: { [key: string]: boolean };
    reservationDetails: { [key: string]: boolean };
    guestData: { [key: string]: boolean };
}

interface ColItemType {
    name: string;
    colId: string;
    isChecked: boolean;
}

export const CustomizeColumnsSection = ({
    gridRef,
    experience,
    context = DashboardMode.LIST,
}: Except<CustomizeColumnsSectionProps, "onClose">) => {
    const isTableView = context === DashboardMode.TABLE;

    const popover = useModal();

    useEffect(() => {
        if (popover.isVisible) {
            const searchInput = document.querySelector<HTMLInputElement>("#customize-columns-search");

            if (searchInput) {
                searchInput.focus();
            }
        }
    }, [popover.isVisible]);

    return (
        <>
            {isTableView ? (
                <SecondaryButton
                    size="small"
                    className="text-bold ml-auto mr-2 items-center px-1.5 text-sm"
                    onClick={popover.toggle}
                >
                    <CustomizeColumnsIcon size="medium" />
                </SecondaryButton>
            ) : (
                <Button variant="outline" icon={<MagicIcon />} color="secondary" onClick={popover.toggle}>
                    Customize
                </Button>
            )}
            {popover.isVisible && (
                <CustomizeColumnsSectionPopover
                    gridRef={gridRef}
                    experience={experience}
                    context={context}
                    onClose={popover.toggle}
                />
            )}
        </>
    );
};

export const CustomizeColumnsSectionPopover = ({
    gridRef,
    experience,
    context = DashboardMode.LIST,
    onClose,
}: CustomizeColumnsSectionProps) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [formUniqueFields, setFormUniqueFields] = useState<ColItemType[] | []>([]);
    const isTableView = context === DashboardMode.TABLE;

    const { setColumnState, setIsColumnDefsCustomized } = useDashboardTableStore((state) => state.actions);

    const { data: forms, isLoading: isFormsLoading } = useForms();

    const combinedReservationDetails = getCombinedReservationDetails(experience);

    const handleSearchChange = (searchResult: string) => {
        setSearchTerm(searchResult.trim());
    };

    const getInitialValues = (data: ColItemType[]) => {
        const initialValues: Record<string, boolean> = {};

        const gridColumns = gridRef?.current?.api?.getColumns();

        if (gridColumns?.length) {
            for (const item of data) {
                const col = gridColumns.find((c) => c.getColDef().colId === item.colId);
                initialValues[item.colId] = col ? col.isVisible() : !!item.isChecked;
            }
            return initialValues;
        }

        const tableColumns = useDashboardTableStore.getState().columnState;
        for (const item of data) {
            const col = tableColumns?.find((c) => c.colId === item.colId);
            initialValues[item.colId] = col ? !col.hide : false;
        }

        return initialValues;
    };

    const handleSubmit = (values: InitialValues) => {
        const gridColumns = gridRef?.current?.api?.getColumns();
        const newColumnStates = getColumnStatesWithLastFlex(values, gridColumns ?? []);

        if (gridRef?.current) {
            const allFlattenedData = [
                EVENT_DETAILS_DATA,
                PURCHASE_DETAILS_DATA,
                combinedReservationDetails,
                GUEST_DATA,
                formUniqueFields,
            ].flat();

            for (const [, sectionValues] of Object.entries(values)) {
                for (const [colId, visible] of Object.entries(sectionValues)) {
                    const columnToUpdate = allFlattenedData.find((col) => col.colId === colId);

                    const columnApi = gridColumns?.find((column) => column.getColDef().colId === columnToUpdate?.colId);

                    if (columnApi) {
                        newColumnStates.push({
                            colId: columnApi.getColId(),
                            hide: !visible,
                        });
                    }
                }
            }
            if (newColumnStates.length > 0) {
                gridRef.current.api.applyColumnState({ state: newColumnStates, applyOrder: false });

                if (isTableView) {
                    setIsColumnDefsCustomized(true);
                    setColumnState(gridRef.current.api.getColumnState());
                }
            }
        }

        onClose();
    };

    useEffect(() => {
        if (isFormsLoading || !forms) {
            return;
        }

        const uniqueFields = getFormUniqFields(forms).map((uniqueField) => ({
            name: uniqueField.label,
            colId: snakeCase(uniqueField.label),
            isChecked: false,
        }));

        setFormUniqueFields(uniqueFields);
    }, [isFormsLoading, forms]);

    if (isFormsLoading || !formUniqueFields) {
        return <Loader />;
    }

    return (
        <Popover visible maxWidth={400} placement="auto" onClickOutside={onClose}>
            <Popover.Content>
                <div className="min-w-[400px] ">
                    <div className="flex flex-col space-y-2 p-4">
                        <h2>{isTableView ? "Column Customization" : "Roster Customization"}</h2>
                        <SearchInput
                            id="customize-columns-search"
                            className="w-full"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>

                    <XolaFormik
                        // Why used key: Once new template applied this form should be re-rendered to get correct initial values
                        key="popover-open"
                        enableReinitialize
                        initialValues={{
                            eventDetails: getInitialValues(EVENT_DETAILS_DATA),
                            purchaseDetails: getInitialValues(PURCHASE_DETAILS_DATA),
                            reservationDetails: getInitialValues(combinedReservationDetails),
                            guestData: getInitialValues(GUEST_DATA),
                            questionnaire: getInitialValues(formUniqueFields),
                        }}
                        onSubmit={handleSubmit}
                    >
                        {({ handleSubmit, setFieldValue, values }) => {
                            return (
                                <div>
                                    <div className="flex max-h-[500px] flex-col overflow-auto">
                                        {isTableView && shouldSectionBeVisible(EVENT_DETAILS_DATA, searchTerm) && (
                                            <CustomizeColumnsInnerSection
                                                name="Event Details"
                                                sectionKey="eventDetails"
                                                values={values}
                                                items={filterItemsByName(EVENT_DETAILS_DATA, searchTerm)}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {shouldSectionBeVisible(PURCHASE_DETAILS_DATA, searchTerm) && (
                                            <CustomizeColumnsInnerSection
                                                name="Purchase Details"
                                                sectionKey="purchaseDetails"
                                                values={values}
                                                items={filterItemsByName(PURCHASE_DETAILS_DATA, searchTerm)}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {shouldSectionBeVisible(combinedReservationDetails, searchTerm) && (
                                            <CustomizeColumnsInnerSection
                                                name="Reservation Details"
                                                sectionKey="reservationDetails"
                                                values={values}
                                                items={filterItemsByName(combinedReservationDetails, searchTerm)}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {shouldSectionBeVisible(GUEST_DATA, searchTerm) && (
                                            <CustomizeColumnsInnerSection
                                                name="Guest Data"
                                                sectionKey="guestData"
                                                values={values}
                                                items={filterItemsByName(GUEST_DATA, searchTerm)}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {shouldSectionBeVisible(formUniqueFields, searchTerm) && (
                                            <CustomizeColumnsInnerSection
                                                name="Questionnaire"
                                                sectionKey="questionnaire"
                                                values={values}
                                                items={filterItemsByName(formUniqueFields, searchTerm)}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                    </div>

                                    <div className="flex justify-end space-x-2 p-4">
                                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                        <Button type="submit" onClick={handleSubmit}>
                                            Apply
                                        </Button>
                                    </div>
                                </div>
                            );
                        }}
                    </XolaFormik>
                </div>
            </Popover.Content>
        </Popover>
    );
};

interface CustomizeColumnsModalProps {
    name: string;
    sectionKey: string;
    values: {
        [key: string]: { [key: string]: boolean };
    };
    items: Array<{ name: string; colId: string }>;
    setFieldValue: (key: string, value: any) => void;
}

const CustomizeColumnsInnerSection = ({
    name,
    sectionKey,
    values,
    items,
    setFieldValue,
}: CustomizeColumnsModalProps) => {
    const [isCollapsed, setIsCollapsed] = useState(true);

    const onHeaderClick = () => {
        setIsCollapsed((previous) => !previous);
    };

    return (
        <div className="flex flex-col">
            <div
                className="flex cursor-pointer items-center justify-between bg-secondary-lighter px-4 py-2 font-bold"
                onClick={onHeaderClick}
            >
                <span>{name}</span>
                <ChevronDownIcon className={clsx(isCollapsed && "rotate-180")} />
            </div>

            {isCollapsed && (
                <div className="flex flex-col">
                    {items.map((item) => (
                        <CustomizeColumnItem
                            key={item.name}
                            name={item.name}
                            isChecked={values[sectionKey][item.colId]}
                            toggle={(value: boolean) => setFieldValue(`${sectionKey}.${item.colId}`, value)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface CustomizeColumnItemProps {
    name: string;
    toggle: (value: boolean) => void;
    isChecked: boolean;
}

export const CustomizeColumnItem = ({ name, toggle, isChecked }: CustomizeColumnItemProps) => {
    return (
        <div
            className="flex h-12 w-full items-center border-b border-gray-light p-4 py-2"
            data-testid="customize-columns-option"
        >
            <Switch.Group>
                <Switch isChecked={isChecked} onChange={toggle} />
                <Switch.Label direction="right">
                    <span className="line-clamp-2">{name}</span>
                </Switch.Label>
            </Switch.Group>
        </div>
    );
};

const filterItemsByName = (items: Array<{ name: string; colId: string }>, searchTerm: string) => {
    return items.filter((item) => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
};

const shouldSectionBeVisible = (items: Array<{ name: string; colId: string }>, searchTerm: string) => {
    return filterItemsByName(items, searchTerm).length > 0;
};

const getCombinedReservationDetails = (experience?: Experience | Experience[]) => {
    const pickupFields = experience
        ? Array.isArray(experience)
            ? getPickUpAddOnsUniqFieldsFromMultiple(experience)
            : getPickUpAddOnsUniqFields(experience)
        : [];

    const addOnsIndex = RESERVATION_DETAILS_DATA.findIndex((item) => item.colId === RosterColumnId.ADDONS);

    if (addOnsIndex !== 1) {
        return [
            ...RESERVATION_DETAILS_DATA.slice(0, addOnsIndex + 1),
            ...(pickupFields.length > 0
                ? pickupFields.map((field) => ({
                      name: field.label,
                      colId: field.colId,
                      isChecked: true,
                  }))
                : []),
            ...RESERVATION_DETAILS_DATA.slice(addOnsIndex + 1),
        ];
    }

    return RESERVATION_DETAILS_DATA;
};

const getColumnStatesWithLastFlex = (values: InitialValues, gridColumns: Column[]): ColumnState[] => {
    const visibleByColId: Record<string, boolean> = {};
    for (const section of Object.values(values)) {
        if (!section) continue;
        for (const [colId, v] of Object.entries(section as Record<string, unknown>)) {
            visibleByColId[colId] = !!v;
        }
    }

    // Find last visible column (in the order of gridColumns)
    let lastVisibleId: string | undefined;
    for (const def of gridColumns) {
        const colId = def.getColId();
        if (!colId) continue;
        if (visibleByColId[colId]) lastVisibleId = colId;
    }

    // Build ColumnState[] (only for columns present in values)
    const columnState: ColumnState[] = [];
    for (const def of gridColumns) {
        const colId = def.getColId();
        if (!colId) continue;
        if (colId in visibleByColId) {
            const visible = visibleByColId[colId];
            columnState.push({
                colId,
                hide: !visible,
                flex: visible && colId === lastVisibleId ? 1 : undefined,
            });
        }
    }

    return columnState;
};
